import { Feature } from '../models/feature';
import FeatureFlagService from '@assets/js/services/feature-flag';

const CARD_ENTRY_CARDHOLDER_NAME_FEATURE_NAME = 'cardentry-cardholdername';
const WEB_WALLETS_SUSPENDED_BLOCKCHAIN_ETHEREUM =
  'web-wallets-suspended-blockchain-ethereum';
const GPAY_FOR_MOB_APP = 'gpay-for-mob-app';
const GPAY_FOR_WEB = 'gpay-for-web';
const ZERO_HASH_USER_AGREEMENT_LINK = 'zero-hash-user-agreement-link';
const NEW_WIDGET_LANDING_PAGE = 'new-widget-landing-page';
const POC_BP_PAYPAL = 'bp-paypal-poc-feature';
const CORPORATE_EXCHANGE_RATES = 'corporate-exchange-rates';
const VIP_MANAGER_INFO = 'vip-manager-info';
const TRAVEL_RULE_FOR_SEND_CRYPTO = 'travel-rule-for-send-crypto';
const TRAVEL_RULE_FOR_RECEIVE_CRYPTO = 'travel-rule-for-receive-crypto';
const TRAVEL_RULE_FOR_TRANSACTION_FUNNEL = 'travel-rule-for-transaction-funnel';
const MARKETING_CONSENT_AB = 'marketing-consent-ab';
const PRE_AUTH_CHECK = 'pre-auth-check';

interface FeatureItem {
  name: string;
  enabled: boolean;
}

// Single-flight guard: dedupes concurrent init dispatches (e.g. the one fired at SPA
// boot and the ones awaited by pages) into one request. Without it, a duplicate request
// to the same URL would abort the in-flight one via the api-layer request dedup.
let featureFlagsRequest: Promise<void> | null = null;

interface FeatureState {
  features: FeatureItem[];
  isLoadingFeatureFlags: boolean;
  featureFlagsSettled: boolean;
}

interface FeatureGetters {
  getFeatureByName: (name: string) => Feature;
}

/**
 *  @deprecated now using FeatureFlagService 
 */

export default {
  namespaced: true,
  state: {
    features: [],
    isLoadingFeatureFlags: false,
    featureFlagsSettled: false,
  } as FeatureState,
  getters: {
    isLoadingFeatureFlags: (state: FeatureState): boolean => state.isLoadingFeatureFlags,
    featureFlagsSettled: (state: FeatureState): boolean => state.featureFlagsSettled,
    features: (state: FeatureState): FeatureItem[] => state.features,
    getFeatureByName: (state: FeatureState) => (name: string): Feature => {
      const feature = state.features.find(feature => feature.name === name);
      if (!feature) {
        return new Feature(false);
      }

      return new Feature(feature.enabled);
    },
    gPayForMobAppFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(GPAY_FOR_MOB_APP);
    },
    gPayForWebFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(GPAY_FOR_WEB);
    },
    cardholderNameFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(CARD_ENTRY_CARDHOLDER_NAME_FEATURE_NAME);
    },
    webWalletsSuspendedBlockchainEthereumFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(
        WEB_WALLETS_SUSPENDED_BLOCKCHAIN_ETHEREUM,
      );
    },
    zeroHashLinkFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(ZERO_HASH_USER_AGREEMENT_LINK);
    },
    newWidgetLandingPageFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(NEW_WIDGET_LANDING_PAGE);
    },
    paypalBPFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(POC_BP_PAYPAL);
    },
    corporateExchangeRatesFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(CORPORATE_EXCHANGE_RATES);
    },
    vipManagerInfoFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(VIP_MANAGER_INFO);
    },
    travelRuleForSendCryptoFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(TRAVEL_RULE_FOR_SEND_CRYPTO);
    },
    travelRuleForReceiveCryptoFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(TRAVEL_RULE_FOR_RECEIVE_CRYPTO);
    },
    travelRuleForTransactionFunnelFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(TRAVEL_RULE_FOR_TRANSACTION_FUNNEL);
    },
    marketingConsentAbFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(MARKETING_CONSENT_AB);
    },
    preAuthCheckFeature: (state: FeatureState, getters: FeatureGetters): Feature => {
      return getters.getFeatureByName(PRE_AUTH_CHECK);
    },
  },
  mutations: {
    setIsLoadingFeatureFlags(state: FeatureState, isLoading: boolean): void {
      state.isLoadingFeatureFlags = isLoading;
    },
    setFeatureFlagsSettled(state: FeatureState, settled: boolean): void {
      state.featureFlagsSettled = settled;
    },
    setFeatures(state: FeatureState, payload: FeatureItem[]): void {
      state.features = payload;
    },
  },
  actions: {
    async fetchFeatureFlags({ commit }: { commit: (mutation: string, payload: FeatureItem[]) => void }): Promise<void> {
      const { features } = await FeatureFlagService.getUserFeatureFlags();
      return commit('setFeatures', features);
    },
    init({
      dispatch,
      state,
      commit,
    }: {
      dispatch: (action: string) => Promise<void>;
      state: FeatureState;
      commit: (mutation: string, payload?: boolean) => void;
    }): Promise<void> {
      if (state.features.length > 0) {
        commit('setFeatureFlagsSettled', true);
        return Promise.resolve();
      }
      if (featureFlagsRequest) {
        return featureFlagsRequest;
      }

      commit('setIsLoadingFeatureFlags', true);

      featureFlagsRequest = dispatch('fetchFeatureFlags')
        .finally(() => {
          // Clear the single-flight marker on settle: on success the state guard above
          // takes over; on failure a later dispatch may retry.
          featureFlagsRequest = null;
          commit('setIsLoadingFeatureFlags', false);
          commit('setFeatureFlagsSettled', true);
        });

      return featureFlagsRequest;
    },
  },
};
